// W29 Cégek · W30 Cég szerkesztése · W31 Beállítások · W32 Mezőszerkesztő
// · W33 Értesítési központ

import { t } from '../lib/i18n';
import { nyelvValaszto } from '../lib/language-switch';
import type { SubscriptionView } from '../lib/session';
import type { CompanyBilling } from '../lib/billing';
import { emptyState } from '../lib/components';
import { confirmDialog, openDialog } from '../lib/dialog';
import { h, icon, icons } from '../lib/dom';
import { numberField, selectField, textField, validateAll, type FieldControl } from '../lib/fields';
import { statusLabel, statusMeta, type TaskStatus } from '../lib/model';

// ---------------------------------------------------------------------------
// W29 / W30 — Cégek (SUPER admin)
// ---------------------------------------------------------------------------

export type CompanyView = {
  id: string;
  name: string;
  description?: string;
  maxUser: number;
  userCount: number;
  adminNames: string[];
};

/// Nagy, egyben olvasható mérőszám kártyán — ugyanaz a minta, mint a projekt
/// részleteinél (W20).
function statCard(label: string, value: string, caption: string, color?: string): HTMLElement {
  return h(
    'div',
    { class: 'card pad16 col', style: { gap: '4px', minWidth: '0' } },
    h('span', { class: 'label', text: label }),
    h('span', {
      class: 'mono w7',
      style: { fontSize: '30px', lineHeight: '1.1', color: color ?? 'var(--tp)' },
      text: value,
    }),
    h('span', { class: 'caption', text: caption }),
  );
}

/// Szuperadmin: MINDEN cég előfizetése egy lapon.
///
/// Ez az üzemeltetői nézet — nem a cégadminé. Azt mutatja meg, ami a
/// szolgáltató szempontjából számít: ki van próbaidőn, kinél áll a fizetés, és
/// kinél telik le hamarosan a türelmi idő.
///
/// A HATÁRIDŐ oszlop állapotfüggő, ezért külön kiírjuk, MIT jelent — egy
/// csupasz dátum három különböző dolgot jelenthetne.
export function subscriptionsPage(options: {
  companies: CompanyBilling[];
  error?: string;
}): HTMLElement {
  if (options.error !== undefined) {
    return h(
      'div',
      { class: 'col g24', style: { padding: '20px 24px' } },
      h(
        'div',
        { class: 'card pad16 col g8', style: { maxWidth: '560px' } },
        h('span', { class: 'h3', text: t('billing.overviewTitle') }),
        h('span', { class: 'body-sm', style: { color: 'var(--err)' }, text: options.error }),
      ),
    );
  }

  const szamol = (status: string) =>
    options.companies.filter((c) => c.subscription?.status === status).length;
  // A `subscription` blokk nélküli cégek a RÉGIEK: rájuk a korlátozás sem
  // vonatkozik. Külön számoljuk, mert nem ugyanaz, mint az „aktív".
  const migralatlan = options.companies.filter((c) => c.subscription === undefined).length;

  const cellaStilus = (szeles: string) => ({ width: szeles, flex: 'none' });

  function sor(company: CompanyBilling): HTMLElement {
    const sub = company.subscription;
    const allapot = sub
      ? t(
          {
            trial: 'billing.statusTrial',
            active: 'billing.statusActive',
            past_due: 'billing.statusPastDue',
            locked: 'billing.statusLocked',
          }[sub.status],
        )
      : t('billing.statusNone');

    const cls = sub
      ? { trial: 'st-wait', active: 'st-ok', past_due: 'st-fail', locked: 'st-rej' }[sub.status]
      : 'st-todo';

    return h(
      'div',
      { class: 'lrow', style: { flexWrap: 'nowrap' } },
      h('span', { class: 'col grow', style: { gap: '2px', minWidth: '0' } },
        h('span', { class: 'body-sm w6', style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, text: company.name }),
        h('span', { class: 'caption mono', style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }, text: company.id }),
      ),
      h('span', { style: cellaStilus('132px') }, h('span', { class: `badge ${cls}`, text: allapot })),
      h('span', {
        class: 'body-sm mono',
        style: cellaStilus('120px'),
        text: sub?.deadline ? sub.deadline.toLocaleDateString('hu-HU') : '—',
      }),
      h('span', {
        class: 'body-sm mono',
        style: cellaStilus('84px'),
        text: String(company.memberCount),
      }),
      h('span', {
        class: 'body-sm mono',
        style: cellaStilus('132px'),
        text: sub ? `${company.memberCount} / ${sub.seats.basic + sub.seats.ai}` : '—',
      }),
      h('span', {
        class: 'body-sm mono',
        style: cellaStilus('120px'),
        text: sub ? `${company.aiAssigned} / ${sub.seats.ai}` : `${company.aiAssigned} / —`,
      }),
    );
  }

  return h(
    'div',
    { class: 'col g16', style: { padding: '24px' } },
    h('span', { class: 'h2', text: t('billing.overviewTitle') }),
    h(
      'div',
      { class: 'grid c4' },
      statCard(t('billing.statusTrial'), String(szamol('trial')), t('billing.trialCaption')),
      statCard(t('billing.statusActive'), String(szamol('active')), t('billing.activeCaption')),
      statCard(
        t('billing.statusPastDue'),
        String(szamol('past_due')),
        t('billing.pastDueCaption'),
        szamol('past_due') > 0 ? 'var(--err)' : undefined,
      ),
      statCard(t('billing.statusLocked'), String(szamol('locked')), t('billing.lockedCaption')),
    ),
    migralatlan > 0
      ? h('span', { class: 'body-sm tm', text: t('billing.legacyNote', [String(migralatlan)]) })
      : null,
    h(
      'div',
      { class: 'panel dense table-scroll-x' },
      h(
        'div',
        { class: 'thead', style: { flexWrap: 'nowrap' } },
        h('span', { class: 'th', style: { flex: '1' }, text: t('billing.colCompany') }),
        h('span', { class: 'th', style: cellaStilus('132px'), text: t('billing.colStatus') }),
        h('span', { class: 'th', style: cellaStilus('120px'), text: t('billing.colDeadline') }),
        h('span', { class: 'th', style: cellaStilus('84px'), text: t('billing.colMembers') }),
        h('span', { class: 'th', style: cellaStilus('132px'), text: t('billing.colSeats') }),
        h('span', { class: 'th', style: cellaStilus('120px'), text: t('billing.colAiSeats') }),
      ),
      ...options.companies.map(sor),
    ),
  );
}

export function companiesPage(options: {
  companies: CompanyView[];
  onNew: () => void;
  onEdit: (company: CompanyView) => void;
}): HTMLElement {
  const total = options.companies.length;
  const users = options.companies.reduce((sum, c) => sum + c.userCount, 0);
  const seats = options.companies.reduce((sum, c) => sum + c.maxUser, 0);
  const fullCompanies = options.companies.filter((c) => c.userCount >= c.maxUser);
  const admins = new Set(options.companies.flatMap((c) => c.adminNames));

  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    h(
      'div',
      {
        class: 'row between',
        style: { padding: '12px 20px', borderBottom: '1px solid var(--border)' },
      },
      h('h2', { class: 'h3', text: t('general.companies') }),
      h('button', {
        class: 'btn btn-primary btn-sm',
        type: 'button',
        text: t('admin.newCompany'),
        onClick: options.onNew,
      }),
    ),
    h(
      'div',
      { class: 'col g16', style: { padding: '20px', gap: '16px' } },
      // A terv negy merőszam-kartyat mutat a lista folott. Eddig semmilyen
      // osszesites nem volt: a kvota betelese csak kartyankent latszott.
      h(
        'div',
        { class: 'grid c4' },
        statCard(
          t('admin.statCompanies'),
          String(total),
          total === 1 ? t('admin.oneCompany') : t('admin.companyCount', [String(total)]),
        ),
        statCard(t('admin.statUsers'), String(users), t('admin.fromSeats', [String(seats)])),
        statCard(
          t('admin.quotaFull'),
          String(fullCompanies.length),
          fullCompanies.length > 0
            ? fullCompanies.slice(0, 2).map((c) => c.name).join(', ')
            : t('admin.seatsAvailableEverywhere'),
          fullCompanies.length > 0 ? 'var(--warn)' : undefined,
        ),
        // A terv „Uj e honapban" kartyat mutat. A cegdokumentum letrehozasi
        // idopontot NEM tarol, ezert helyette az adminok szama all itt —
        // az tudhato, es ugyanolyan hasznos a szuperadminnak.
        statCard(t('admin.statAdmins'), String(admins.size), t('admin.acrossCompanies')),
      ),
      options.companies.length === 0
        ? emptyState({
            title: t('admin.noCompanies'),
            actionLabel: t('admin.newCompany'),
            onAction: options.onNew,
          })
        : h(
            'div',
            { class: 'panel', style: { overflow: 'hidden' } },
            h(
              'div',
              { class: 'thead' },
              h('span', { class: 'th', style: { width: '44px', flex: 'none' } }),
              h('span', { class: 'th', style: { flex: '1' }, text: t('people.rowCompany') }),
              h('span', {
                class: 'th',
                style: { width: '200px', flex: 'none' },
                text: t('admin.colHeadcountQuota'),
              }),
              h('span', { class: 'th', style: { width: '140px', flex: 'none' }, text: t('people.colStatus') }),
              h('span', { class: 'th', style: { width: '40px', flex: 'none' } }),
            ),
            ...options.companies.map((company) => {
              const full = company.userCount >= company.maxUser;
              const ratio =
                company.maxUser > 0
                  ? Math.min(100, Math.round((company.userCount / company.maxUser) * 100))
                  : 0;

              return h(
                'div',
                { class: 'lrow', style: { alignItems: 'center', flexWrap: 'nowrap' } },
                h(
                  'span',
                  {
                    class: 'ic',
                    style: {
                      width: '34px',
                      height: '34px',
                      borderRadius: '9px',
                      background: 'var(--sunken)',
                      color: 'var(--ts)',
                      flex: 'none',
                    },
                  },
                  icon(icons.folder, 17),
                ),
                h(
                  'span',
                  { class: 'col grow', style: { gap: '0', minWidth: '0' } },
                  h('span', { class: 'body-sm w6', text: company.name }),
                  h('span', {
                    class: 'caption',
                    text:
                      company.description ||
                      (company.adminNames.length > 0
                        ? t('admin.adminPrefix', [company.adminNames.join(', ')])
                        : t('admin.noAdmin')),
                  }),
                ),
                h(
                  'span',
                  { class: 'col', style: { width: '200px', flex: 'none', gap: '4px' } },
                  h('span', {
                    class: 'caption mono',
                    text: t('admin.userQuota', [String(company.userCount), String(company.maxUser)]),
                  }),
                  h(
                    'div',
                    {
                      class: 'prog',
                      role: 'progressbar',
                      'aria-valuenow': String(company.userCount),
                      'aria-valuemin': '0',
                      'aria-valuemax': String(company.maxUser),
                    },
                    h('i', {
                      style: { width: `${ratio}%`, background: full ? 'var(--err)' : undefined },
                    }),
                  ),
                ),
                h(
                  'span',
                  { style: { width: '140px', flex: 'none' } },
                  h('span', {
                    // A betelt letszamot jelezni kell: uj felhasznalo felvetele
                    // ilyenkor elbukna.
                    class: `badge ${full ? 'st-wait' : 'st-ok'}`,
                    style: { height: '22px', fontSize: '11px' },
                    text: full ? t('admin.quotaFull') : t('admin.companyActive'),
                  }),
                ),
                h(
                  'button',
                  {
                    class: 'btn btn-ghost btn-icon btn-sm',
                    type: 'button',
                    'aria-label': t('admin.editNamed', [company.name]),
                    style: { flex: 'none' },
                    onClick: () => options.onEdit(company),
                  },
                  h('span', { class: 'ic' }, icon(icons.pencil)),
                ),
              );
            }),
          ),
    ),
  );
}

export function openCompanyDialog(options: {
  company?: CompanyView;
  onSubmit: (company: { name: string; description: string; maxUser: number }) => void;
}): void {
  const name = textField({
    label: t('register.companyName'),
    required: true,
    value: options.company?.name,
  });
  const description = textField({
    label: t('general.description'),
    multiline: true,
    value: options.company?.description,
    maxLength: 500,
  });
  const maxUser = numberField({
    label: t('admin.maxHeadcount'),
    required: true,
    min: 1,
    mono: true,
  });
  if (options.company) maxUser.input.value = String(options.company.maxUser);

  const used = options.company?.userCount ?? 0;
  const quotaCard = h('div', { class: 'card pad16 col', style: { gap: '10px' } });

  function renderQuota(): void {
    const limit = Number(maxUser.input.value) || options.company?.maxUser || 0;
    const ratio = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;
    quotaCard.replaceChildren(
      h('span', { class: 'label', text: t('admin.quotaCardTitle') }),
      h(
        'div',
        {
          class: 'prog',
          style: { height: '10px' },
          role: 'progressbar',
          'aria-valuenow': String(used),
          'aria-valuemin': '0',
          'aria-valuemax': String(limit),
        },
        h('i', { style: { width: `${ratio}%`, background: used >= limit ? 'var(--err)' : undefined } }),
      ),
      h('span', {
        class: 'caption',
        text: t('admin.quotaLine', [
          String(used),
          String(limit),
          String(Math.max(0, limit - used)),
        ]),
      }),
    );
  }
  renderQuota();
  maxUser.input.addEventListener('input', renderQuota);

  openDialog({
    title: options.company ? t('settings.companyData') : t('admin.newCompany'),
    // A terv 880 px-es, ketoszlopos urlapot mutat.
    width: '880px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        h('span', { class: 'h3', text: t('settings.companyData') }),
        h('div', { class: 'grid c2', style: { gap: '16px 20px' } }, name.element, maxUser.element),
        description.element,
        options.company ? quotaCard : null,
      ),
    footer: (close) => [
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!validateAll([name, maxUser] as FieldControl<HTMLElement>[])) return;
          const limit = Number(maxUser.input.value);
          if (options.company && limit < options.company.userCount) {
            // A kvotat nem lehet a jelenlegi letszam ala vinni: a tobbletet
            // valakinek el kellene tavolitania, azt nem dontjuk el helyette.
            maxUser.setError(
              t('admin.maxUserBelowCurrent', [String(options.company.userCount)]),
            );
            maxUser.input.focus();
            return;
          }
          options.onSubmit({
            name: name.input.value.trim(),
            description: description.input.value.trim(),
            maxUser: limit,
          });
          close();
        },
      }),
    ],
  });
}

// ---------------------------------------------------------------------------
// W31 — Beállítások központ
// ---------------------------------------------------------------------------

export function settingsPage(options: {
  onOpenTemplates: () => void;
  onOpenFields: () => void;
  onOpenJobTitles: () => void;
  onOpenGroups: () => void;
  onOpenCompany?: () => void;
  /// Darabszámok a sorok alcíméhez — a terv minden sor alá kiírja, mennyi
  /// elem van mögötte. Eddig általános leírás állt ott, ami minden cégnél
  /// ugyanaz volt.
  counts?: {
    templates?: number;
    fields?: number;
    jobTitles?: number;
    people?: number;
    groups?: number;
  };
  companyName?: string;
  /// Az előfizetés állapota. Hiányozhat: a régebbi cégeknek nincs ilyen
  /// blokkjuk, és ilyenkor a kártya EL SEM KÉSZÜL — kitalált állapotot nem
  /// írunk ki.
  subscription?: SubscriptionView;
  /// Hány helyet osztottak már ki. A megvásárolt kerettel együtt ez mutatja
  /// meg, van-e még szabad AI-s hely.
  seatUsage?: { basic: number; ai: number };
  /// Csak cégadminnak: a fizetés indítása. Hiányában a kártya csak tájékoztat.
  onBuySeats?: () => void;
  theme: 'system' | 'light' | 'dark';
  onThemeChange: (theme: 'system' | 'light' | 'dark') => void;
  /// Megadta-e a felhasználó az AI-feldolgozáshoz a hozzájárulást, és mi
  /// történjen, ha megnyomja a kapcsolót. Az engedélynek VISSZAVONHATÓNAK kell
  /// lennie — az App Review ezt is elvárja, nem csak a megkérdezést.
  aiConsentGranted: boolean;
  onAiConsentToggle: () => void;
}): HTMLElement {
  const themes: { key: 'system' | 'light' | 'dark'; label: string }[] = [
    { key: 'light', label: t('people.themeLight') },
    { key: 'dark', label: t('people.themeDark') },
    { key: 'system', label: 'Rendszer' },
  ];

  /// A terv sorai: ikoncsempe, cím, alcím a darabszámmal, jobbra nyíl.
  function row(
    iconPath: string,
    title: string,
    caption: string,
    onClick: () => void,
  ): HTMLElement {
    return h(
      'div',
      { class: 'panel', style: { overflow: 'hidden' } },
      h(
        'button',
        {
          type: 'button',
          class: 'lrow',
          style: {
            width: '100%',
            border: '0',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            textAlign: 'left',
            alignItems: 'center',
          },
          onClick,
        },
        h(
          'span',
          {
            class: 'ic',
            style: {
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: 'var(--sunken)',
              color: 'var(--ts)',
              flex: 'none',
            },
          },
          icon(iconPath, 18),
        ),
        h(
          'span',
          { class: 'col grow', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
          h('span', { class: 'body-sm w6', text: title }),
          h('span', { class: 'caption', text: caption }),
        ),
        h('span', { class: 'ic tm', style: { flex: 'none' } }, icon(icons.arrowRight, 15)),
      ),
    );
  }

  const counts = options.counts ?? {};
  /// A darabszamos alcim. Ha meg nincs szam, a puszta szoveget adja —
  /// ezert all a `%s` helyen ures string.
  const pluralCount = (n: number | undefined, key: string) =>
    t(key, [n === undefined ? '' : String(n)]).trim();

  /// Az előfizetés kártyája.
  ///
  /// A HATÁRIDŐ állapotfüggő, és ezt ki is mondjuk: próbaidőnél a próbaidő
  /// vége, fizetett időszaknál a következő fordulónap, türelmi időben pedig
  /// az a nap, ameddig rendezni kell. Egy puszta dátum félreérthető lenne.
  function subscriptionCard(sub: SubscriptionView): HTMLElement {
    const chipClass = {
      trial: 'st-wait',
      active: 'st-ok',
      past_due: 'st-fail',
      locked: 'st-rej',
    }[sub.status];

    const hatarido = sub.deadline
      ? t(
          {
            trial: 'billing.trialUntil',
            active: 'billing.renewsAt',
            past_due: 'billing.payUntil',
            locked: 'billing.lockedSince',
          }[sub.status],
          [sub.deadline.toLocaleDateString('hu-HU')],
        )
      : undefined;

    const used = options.seatUsage;

    return h(
      'div',
      { class: 'card pad16 col g12' },
      h(
        'div',
        { class: 'row between g8', style: { alignItems: 'center', flexWrap: 'wrap' } },
        h('span', { class: 'h3', text: t('billing.title') }),
        h('span', {
          class: `badge ${chipClass}`,
          // Lapos kulcsok: a szotar egyszintu terkep, nem tudna a
          // `billing.status.trial` harom szintjet feloldani.
          text: t(
            {
              trial: 'billing.statusTrial',
              active: 'billing.statusActive',
              past_due: 'billing.statusPastDue',
              locked: 'billing.statusLocked',
            }[sub.status],
          ),
        }),
      ),
      hatarido ? h('span', { class: 'body-sm tm', text: hatarido }) : null,
      h('span', {
        class: 'body-sm',
        text: t('billing.seatsLine', [
          used ? String(used.basic) : '?',
          String(sub.seats.basic),
          used ? String(used.ai) : '?',
          String(sub.seats.ai),
        ]),
      }),
      // A türelmi idő alatt az ÍRÁS tiltott — ezt kimondjuk, különben a
      // felhasználó csak annyit látna, hogy „nem megy semmi".
      sub.status === 'past_due'
        ? h('span', { class: 'body-sm', style: { color: 'var(--err)' }, text: t('billing.readOnly') })
        : null,
      sub.status === 'locked'
        ? h('span', { class: 'body-sm', style: { color: 'var(--err)' }, text: t('billing.lockedHelp') })
        : null,
      options.onBuySeats
        ? h('div', { class: 'row' },
            h('button', {
              class: 'btn btn-primary',
              type: 'button',
              text: sub.seats.basic + sub.seats.ai > 0
                ? t('billing.manage')
                : t('billing.buy'),
              onClick: options.onBuySeats,
            }))
        : null,
    );
  }

  return h(
    'div',
    {
      class: 'col g16',
      style: { padding: '24px', maxWidth: '980px', marginLeft: 'auto', marginRight: 'auto' },
    },
    options.subscription ? subscriptionCard(options.subscription) : null,
    h(
      'div',
      { class: 'col', style: { gap: '2px' } },
      h('h2', { class: 'h3', text: t('general.settings') }),
      h('span', {
        class: 'caption',
        text: t('settings.appliesToCompany'),
      }),
    ),
    h(
      'div',
      { class: 'grid c2', style: { gap: '12px' } },
      row(
        icons.folder,
        t('settings.templates'),
        t('settings.templatesCaption', [pluralCount(counts.templates, 'settings.templateCount')]),
        options.onOpenTemplates,
      ),
      row(
        icons.pencil,
        t('report.contentFields'),
        t('settings.fieldsCaption', [pluralCount(counts.fields, 'settings.fieldCount')]),
        options.onOpenFields,
      ),
      row(
        icons.users,
        t('people.tabJobTitles'),
        pluralCount(counts.jobTitles, 'settings.jobTitleCount'),
        options.onOpenJobTitles,
      ),
      row(
        icons.users,
        t('settings.usersAndGroups'),
        `${pluralCount(counts.people, 'settings.peopleCount')} · ${pluralCount(
          counts.groups,
          'settings.groupCount',
        )}`,
        options.onOpenGroups,
      ),
      options.onOpenCompany
        ? row(
            icons.shield,
            t('settings.companyData'),
            options.companyName ?? t('settings.nameAndSeats'),
            options.onOpenCompany,
          )
        : null,
    ),
    h(
      'div',
      { class: 'card pad16 col', style: { gap: '12px' } },
      h('span', { class: 'label', text: t('settings.themeAndLook') }),
      h(
        'div',
        { class: 'row g12', style: { alignItems: 'center' } },
        h('span', { class: 'body-sm grow', text: t('people.theme') }),
        h(
          'div',
          { class: 'seg' },
          ...themes.map((theme) =>
            h('button', {
              type: 'button',
              class: options.theme === theme.key ? 'on' : '',
              'aria-pressed': options.theme === theme.key ? 'true' : 'false',
              text: theme.label,
              onClick: () => options.onThemeChange(theme.key),
            }),
          ),
        ),
      ),
      // A NYELV A TÉMA MELLÉ kerül: mindkettő a felület megjelenését szabja,
      // és a felhasználó ugyanitt keresi. A váltás újratölti a lapot — ezért
      // van alatta a figyelmeztetés, hogy ne tűnjön hibának.
      h(
        'div',
        { class: 'row g12', style: { alignItems: 'center' } },
        h('span', { class: 'body-sm grow', text: t('settings.language') }),
        nyelvValaszto(),
      ),
    ),
    // AZ AI-HOZZÁJÁRULÁS SAJÁT KÁRTYÁT KAP, nem a téma mellé kerül: ez nem
    // megjelenés kérdése, hanem az, hogy kimegy-e adat egy külső
    // szolgáltatóhoz. Az állapot és a visszavonás egy helyen látszik.
    h(
      'div',
      { class: 'card pad16 col', style: { gap: '10px' } },
      h('span', { class: 'label', text: t('settings.aiConsent') }),
      h('span', {
        class: 'body-sm ts',
        text: options.aiConsentGranted
          ? t('settings.aiConsentOn')
          : t('settings.aiConsentOff'),
      }),
      h('button', {
        class: 'btn btn-secondary btn-sm',
        style: { alignSelf: 'flex-start' },
        type: 'button',
        text: options.aiConsentGranted
          ? t('settings.aiConsentRevoke')
          : t('settings.aiConsentGrant'),
        onClick: options.onAiConsentToggle,
      }),
    ),
  );
}

// ---------------------------------------------------------------------------
// W32 — Dinamikus mező szerkesztő
// ---------------------------------------------------------------------------

export type FieldDefinition = {
  id: string;
  name: string;
  type: 'text' | 'number' | 'date';
  showOnList: boolean;
  requiredInStatus?: TaskStatus;
};

const fieldTypeKeys: Record<FieldDefinition['type'], string> = {
  text: 'task.fieldTypeText',
  number: 'task.fieldTypeNumber',
  date: 'task.fieldTypeDate',
};

/// A mező alcíme a listában: típus, kötelezőség, listamegjelenés.
function fieldCaption(field: FieldDefinition): string {
  return [
    t(fieldTypeKeys[field.type]),
    field.requiredInStatus
      ? t('task.requiredIn', [statusLabel(field.requiredInStatus)])
      : null,
    field.showOnList ? t('task.alsoInList') : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

export function openFieldEditor(options: {
  field?: FieldDefinition;
  onSubmit: (field: Omit<FieldDefinition, 'id'>) => void;
  onDelete?: () => void;
}): void {
  const name = textField({
    label: t('task.fieldName'),
    required: true,
    value: options.field?.name,
  });

  let selectedType: FieldDefinition['type'] = options.field?.type ?? 'text';
  const typeHost = h('div', { class: 'row g6 wrap' });

  /// A terv chipekkel választ típust, nem legördülővel: a hét típus így
  /// egyszerre látszik, és a választás egy kattintás.
  function renderTypes(): void {
    typeHost.replaceChildren(
      ...(Object.keys(fieldTypeKeys) as FieldDefinition['type'][]).map((type) =>
        h('button', {
          type: 'button',
          class: `chip${type === selectedType ? ' on' : ''}`,
          'aria-pressed': type === selectedType ? 'true' : 'false',
          style: { font: 'inherit', cursor: 'pointer' },
          text: t(fieldTypeKeys[type]),
          onClick: () => {
            selectedType = type;
            renderTypes();
          },
        }),
      ),
    );
  }
  renderTypes();

  const status = selectField({
    label: t('task.fieldRequiredInStatus'),
    value: options.field?.requiredInStatus ?? '',
    choices: [
      { value: '', label: t('task.neverRequiredOption') },
      ...(Object.keys(statusMeta) as TaskStatus[]).map((s) => ({
        value: s,
        label: statusLabel(s),
      })),
    ],
  });

  let showOnList = options.field?.showOnList ?? false;
  const showBox = h('span', { class: `sw${showOnList ? ' on' : ''}`, 'aria-hidden': 'true' });

  openDialog({
    title: options.field ? t('task.editField') : t('task.newField'),
    width: '560px',
    body: () =>
      h(
        'div',
        { class: 'col g16' },
        name.element,
        h('div', { class: 'field' }, h('span', { class: 'flbl', text: t('task.type') }), typeHost),
        status.element,
        h(
          'label',
          {
            class: 'row g12',
            style: { cursor: 'pointer', alignItems: 'center', position: 'relative' },
          },
          h('input', {
            type: 'checkbox',
            checked: showOnList ? 'checked' : undefined,
            style: { position: 'absolute', opacity: '0', width: '46px', height: '26px', margin: '0' },
            onChange: (event: Event) => {
              showOnList = (event.target as HTMLInputElement).checked;
              showBox.className = `sw${showOnList ? ' on' : ''}`;
            },
          }),
          showBox,
          h(
            'span',
            { class: 'col', style: { gap: '0' } },
            h('span', { class: 'body-sm w6', text: t('task.fieldOnHeader') }),
            h('span', {
              class: 'caption',
              text: t('task.fieldOnHeaderHint'),
            }),
          ),
        ),
      ),
    footer: (close) => [
      options.onDelete
        ? h('button', {
            class: 'btn btn-ghost',
            type: 'button',
            style: { color: 'var(--err)' },
            text: t('general.delete'),
            onClick: () => {
              // A mezo torlese a mar kitoltott ertekeket is erinti.
              confirmDialog({
                title: t('task.deleteFieldTitle'),
                message:
                  t('task.deleteFieldMessage'),
                confirmLabel: t('general.delete'),
                danger: true,
                onConfirm: () => {
                  options.onDelete?.();
                  close();
                },
              });
            },
          })
        : h('span'),
      h('span', { class: 'grow' }),
      h('button', { class: 'btn btn-secondary', type: 'button', text: t('general.cancel'), onClick: close }),
      h('button', {
        class: 'btn btn-primary',
        type: 'button',
        text: t('general.save'),
        onClick: () => {
          if (!validateAll([name] as FieldControl<HTMLElement>[])) return;
          options.onSubmit({
            name: name.input.value.trim(),
            type: selectedType,
            showOnList,
            requiredInStatus: (status.input.value || undefined) as TaskStatus | undefined,
          });
          close();
        },
      }),
    ],
  });
}

export function fieldsPage(options: {
  fields: FieldDefinition[];
  onNew: () => void;
  onEdit: (field: FieldDefinition) => void;
}): HTMLElement {
  return h(
    'div',
    { class: 'col', style: { gap: '0' } },
    h(
      'div',
      {
        class: 'row between',
        style: { padding: '12px 20px', borderBottom: '1px solid var(--border)' },
      },
      h(
        'div',
        { class: 'col', style: { gap: '2px' } },
        h('h2', { class: 'h3', text: t('report.contentFields') }),
        h('span', {
          class: 'caption',
          text: t('task.fieldsPageHint'),
        }),
      ),
      h('button', {
        class: 'btn btn-primary btn-sm',
        type: 'button',
        text: t('task.newField'),
        onClick: options.onNew,
      }),
    ),
    h(
      'div',
      { style: { padding: '20px' } },
      options.fields.length === 0
        ? emptyState({
            title: t('task.noCustomFieldsTitle'),
            text: t('task.noCustomFieldsText'),
            actionLabel: t('task.newField'),
            onAction: options.onNew,
          })
        : h(
            'div',
            { class: 'panel', style: { overflow: 'hidden', maxWidth: '760px' } },
            ...options.fields.map((field) =>
              h(
                'button',
                {
                  type: 'button',
                  class: 'lrow',
                  style: {
                    width: '100%',
                    border: '0',
                    borderBottom: '1px solid var(--border)',
                    font: 'inherit',
                    color: 'inherit',
                    cursor: 'pointer',
                    textAlign: 'left',
                    alignItems: 'center',
                  },
                  onClick: () => options.onEdit(field),
                },
                h('span', { class: 'ic tm', style: { flex: 'none' } }, icon(icons.drag)),
                h(
                  'span',
                  { class: 'col grow', style: { gap: '0', minWidth: '0', alignItems: 'flex-start' } },
                  h('span', { class: 'body-sm w6', text: field.name }),
                  h('span', { class: 'caption', text: fieldCaption(field) }),
                ),
                h('span', { class: 'ic tm', style: { flex: 'none' } }, icon(icons.pencil)),
              ),
            ),
          ),
    ),
  );
}

// ---------------------------------------------------------------------------
// W33 — Értesítési központ
// ---------------------------------------------------------------------------

export type NotificationView = {
  id: string;
  title: string;
  body: string;
  sentAt: Date;
  seen: boolean;
  taskId?: string;
  /// Megjegyzés-értesítés, ha van hozzá `commentId`. A többi fajtát a
  /// dokumentum NEM különbözteti meg.
  commentId?: string;
};

/// Relatív idő a terv szerint: „12 perce", „1 órája", „Tegnap 17:20", „09. 20."
function relativeTime(sentAt: Date, now = new Date()): string {
  const minutes = Math.round((now.getTime() - sentAt.getTime()) / 60000);
  if (minutes < 1) return t('noti.justNowLong');
  if (minutes < 60) return t('noti.minutesAgo', [String(minutes)]);

  const hours = Math.round(minutes / 60);
  const sameDay =
    sentAt.getFullYear() === now.getFullYear() &&
    sentAt.getMonth() === now.getMonth() &&
    sentAt.getDate() === now.getDate();
  if (sameDay && hours < 12) return t('noti.hoursAgo', [String(hours)]);

  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(sentAt.getHours())}:${pad(sentAt.getMinutes())}`;

  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (
    sentAt.getFullYear() === yesterday.getFullYear() &&
    sentAt.getMonth() === yesterday.getMonth() &&
    sentAt.getDate() === yesterday.getDate()
  ) {
    return t('noti.yesterdayAt', [time]);
  }
  if (sameDay) return time;
  return `${pad(sentAt.getMonth() + 1)}. ${pad(sentAt.getDate())}.`;
}

export function notificationsPage(options: {
  notifications: NotificationView[];
  tab?: 'unread' | 'all';
  onTabChange?: (tab: 'unread' | 'all') => void;
  onOpen: (notification: NotificationView) => void;
  onMarkAllSeen: () => void;
}): HTMLElement {
  const unseen = options.notifications.filter((n) => !n.seen).length;
  const tab = options.tab ?? 'unread';
  const shown =
    tab === 'unread' ? options.notifications.filter((n) => !n.seen) : options.notifications;

  /// A terv színkódolt ikoncsempét mutat értesítés-fajtánként (új feladat,
  /// megjelölés, határidő, elfogadás, hiba, projekt lezárult). A
  /// dokumentumon FAJTA nincs, csak `commentId` — abból a megjegyzés-fajta
  /// derül ki. A többinél semleges csempe áll, kitalált besorolás helyett.
  function tile(notification: NotificationView): HTMLElement {
    const isComment = Boolean(notification.commentId);
    return h(
      'span',
      {
        class: 'ic',
        style: {
          width: '34px',
          height: '34px',
          borderRadius: '10px',
          flex: 'none',
          background: isComment ? 'var(--info-soft)' : 'var(--accent-soft)',
          color: isComment ? 'var(--info)' : 'var(--accent)',
        },
      },
      icon(isComment ? icons.comment : icons.bell, 17),
    );
  }

  return h(
    'div',
    {
      class: 'col g14',
      style: { padding: '24px', maxWidth: '820px', marginLeft: 'auto', marginRight: 'auto', gap: '14px' },
    },
    h(
      'div',
      { class: 'row between g12', style: { alignItems: 'center', flexWrap: 'wrap' } },
      h(
        'div',
        { class: 'tabs', role: 'tablist' },
        h(
          'button',
          {
            class: `tab${tab === 'unread' ? ' on' : ''}`,
            type: 'button',
            role: 'tab',
            'aria-selected': tab === 'unread' ? 'true' : 'false',
            onClick: () => options.onTabChange?.('unread'),
          },
          h('span', { text: t('noti.unseen') }),
          h('span', { class: 'mono tm', text: String(unseen) }),
        ),
        h(
          'button',
          {
            class: `tab${tab === 'all' ? ' on' : ''}`,
            type: 'button',
            role: 'tab',
            'aria-selected': tab === 'all' ? 'true' : 'false',
            onClick: () => options.onTabChange?.('all'),
          },
          h('span', { text: t('dashboard.showAll') }),
          h('span', { class: 'mono tm', text: String(options.notifications.length) }),
        ),
      ),
      unseen > 0
        ? h('button', {
            class: 'btn btn-secondary btn-sm',
            type: 'button',
            text: t('noti.markAllSeen'),
            onClick: options.onMarkAllSeen,
          })
        : h('span'),
    ),
    shown.length === 0
      ? emptyState({
          title: tab === 'unread' ? t('noti.noUnread') : t('noti.noNoti'),
        })
      : h(
          'div',
          { class: 'panel', style: { overflow: 'hidden' } },
          ...shown.map((notification) =>
            h(
              'button',
              {
                type: 'button',
                class: 'lrow',
                style: {
                  width: '100%',
                  border: '0',
                  borderBottom: '1px solid var(--border)',
                  textAlign: 'left',
                  font: 'inherit',
                  color: 'inherit',
                  cursor: 'pointer',
                  alignItems: 'flex-start',
                  padding: '12px 14px',
                  // Az olvasatlan sor kiemelve, hogy egy pillantassal latszodjon.
                  background: notification.seen ? undefined : 'var(--accent-soft)',
                },
                onClick: () => options.onOpen(notification),
              },
              tile(notification),
              h(
                'span',
                { class: 'col grow', style: { gap: '2px', minWidth: '0', alignItems: 'flex-start' } },
                h('span', { class: 'body-sm w6', text: notification.title }),
                h('span', {
                  class: 'caption',
                  style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                  text: notification.body,
                }),
              ),
              h(
                'span',
                {
                  class: 'col',
                  style: { alignItems: 'flex-end', gap: '6px', flex: 'none' },
                },
                h('span', { class: 'caption', text: relativeTime(notification.sentAt) }),
                notification.seen
                  ? null
                  : h('span', {
                      'aria-label': 'Olvasatlan',
                      style: {
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'var(--accent)',
                      },
                    }),
              ),
            ),
          ),
        ),
    h('span', {
      class: 'caption',
      text: t('noti.rulesInProfile'),
    }),
  );
}
